import { FC } from 'react';
import NextHead from 'next/head';
import { DefaultSeo } from 'next-seo';
import config from '@config/defaultSeo.json';
import { useRouter } from 'next/router';
import { useLocale } from '@hooks/useLocale';
import Script from 'next/script';
import { FEATURE_FLAG_ALBY_CHATBOT } from '@config/feature-flag';

const Head: FC = () => {
  const { pathname } = useRouter();
  const locale = useLocale();
  return (
    <>
      <DefaultSeo {...config} />

      {/* OneTrust Cookies Consent Notice start */}
      <Script
        strategy="beforeInteractive"
        src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
        data-domain-script={process.env.NEXT_PUBLIC_ONETRUST_KEY}
      />
      <Script
        id="One-Trust-Cookies-Consent-Notice"
        strategy="beforeInteractive"
        dangerouslySetInnerHTML={{
          __html: `function OptanonWrapper() { }`,
        }}
      />
      {/* OneTrust Cookies Consent Notice end */}

      {pathname !== '/[locale]/product/[pdp]' && (
        <Script
          strategy="afterInteractive"
          type="text/plain"
          className="optanon-category-C0004"
          src={`https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=${process.env.NEXT_PUBLIC_KLAVIYO_KEY}`}
        />
      )}
      {locale === 'en' && FEATURE_FLAG_ALBY_CHATBOT && (
        <Script
          strategy="afterInteractive"
          type="text/javascript"
          src={`https://cdn.alby.com/apps/launcher/embed.js?brandId=${process.env.NEXT_PUBLIC_ALBY_BRAND_ID}`}
        />
      )}
      <NextHead>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=5"
        />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/favicons/apple-touch-icon.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/favicons/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/favicons/favicon-16x16.png"
        />
        <link rel="manifest" href="/favicons/site.webmanifest" />
        <link
          rel="mask-icon"
          href="/favicons/safari-pinned-tab.svg"
          color="#5bbad5"
        />
        <meta name="msapplication-TileColor" content="#da532c" />
        <meta name="theme-color" content="#ffffff" />

        {/* Preconnect GA and GTM */}
        <link rel="preconnect" href="https://www.google-analytics.com" />
        <link rel="preconnect" href="https://www.googletagmanager.com" />

        <style>
          {`
          @font-face {
            font-family: 'MountainSans';
            font-style: normal;
            font-weight: 400;
            font-display: swap;
            src: url('/fonts/MountainSans-Regular-6c7fa10e482c5b7689dad424ff77105d.woff2') format("woff2"),
            url('/fonts/MountainSans-Regular-e4ce8f3b750353128afb64836699ea9a.woff') format("woff");
          }
          @font-face {
            font-family: 'MountainSans';
            font-style: italic;
            font-weight: 400;
            font-display: swap;
            src: url('/fonts/MountainSans-RegularItalic-b8a0a44666d527c96686581c47d0fe2b.woff2') format("woff2"),
            url('/fonts/MountainSans-RegularItalic-122f8bf7f1ed4afac987dd16372cc7e7.woff') format("woff");
          }

          @font-face {
            font-family: 'MountainSans';
            font-style: normal;
            font-weight: 700;
            font-display: swap;
            src: url('/fonts/MountainSans-Bold-2f1d8997ca073cebf8e99e44023152b9.woff2') format("woff2"),
            url('/fonts/MountainSans-Bold-5deaa17c561f007487c0e1decd312e53.woff') format("woff");
          }
          @font-face {
            font-family: 'MountainSans';
            font-style: italic;
            font-weight: 700;
            font-display: swap;
            src: url('/fonts/MountainSans-BoldItalic-c21d9fedf934e599164e8f3b00e0b83b.woff2') format("woff2"),
            url('/fonts/MountainSans-BoldItalic-5218e6ef797fa1a71de5df2d639273db.woff') format("woff");
          }
          @font-face {
            font-family: 'Helvetica Neue';
            font-style: normal;
            font-weight: 400;
            font-display: swap;
            src: local('Helvetica Neue') url('/fonts/HelveticaNeueLTStd-Roman-94003a0e80103fc3f998ba7a59b2a237.woff2') format("woff2"),
            url('/fonts/HelveticaNeueLTStd-Roman-47b39d054a4241e4ccd868d4005e4492.woff') format("woff");
          }
          @font-face {
            font-family: 'Helvetica Neue';
            font-style: italic;
            font-weight: 400;
            font-display: swap;
            src: local('Helvetica Neue') url('/fonts/HelveticaNeueLTStd-It-e2afe64da94fc2b1d10088d45f20def4.woff2') format("woff2"),
            url('/fonts/HelveticaNeueLTStd-It-66fcb3804d8cca159dcd3c97cf9b27cb.woff') format("woff");
          }
          @font-face {
            font-family: 'Helvetica Neue';
            font-style: normal;
            font-weight: 700;
            font-display: swap;
            src: local('Helvetica Neue') url('/fonts/HelveticaNeueLTStd-Bd-bfdafc71b6847d67aafc1b4bd27f0251.woff2') format("woff2"),
            url('/fonts/HelveticaNeueLTStd-Bd-02df0a0c5c46ba651543bf2cf7cb2dd9.woff') format("woff");
          }
          @font-face {
            font-family: 'Helvetica Neue';
            font-style: italic;
            font-weight: 700;
            font-display: swap;
            src: local('Helvetica Neue') url('/fonts/HelveticaNeueLTStd-BdIt-5e4b3b3ff7018de5e2825de7e815f822.woff2') format("woff2"),
            url('/fonts/HelveticaNeueLTStd-BdIt-789d06e65c13e9b84293f947c7c9736d.woff') format("woff");
          }
          @font-face {
            font-family: 'Avenir Next Condensed Semi Bold';
            font-style: normal;
            font-weight: 700;
            font-display: swap;
            src: url('/fonts/AvenirNextLTPro-DemiCn-624124e11ea86634ebe7be0b4542126e.woff2') format("woff2"),
            url('/fonts/AvenirNextLTPro-DemiCn-44a76fe6432bf58177adff394143334d.woff') format("woff");
          }
        `}
        </style>
      </NextHead>
    </>
  );
};

export default Head;
